import React, { useState, useEffect, useContext } from "react";
import { toast } from "react-toastify";
import ApiURL from "../Api/Api";
import { SidebarContext } from "./SidebarContext";
import Navbar from "./Navbar";
import Sidebar from "./Sidebar";
import { useNavigate } from "react-router-dom";

const ThresholdManagement = () => {
  const { isSidebarOpen } = useContext(SidebarContext);
  const navigate = useNavigate();
  
  const user_id = parseInt(localStorage.getItem("id") || "0");
  const role = localStorage.getItem("role") || "";
  const company_id = parseInt(localStorage.getItem("company_id") || "0");
  
  const [loading, setLoading] = useState(true);
  const [thresholds, setThresholds] = useState({ admins: [], sites: [] });
  const [lowBalanceAlerts, setLowBalanceAlerts] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editingValue, setEditingValue] = useState("");
  const [saving, setSaving] = useState(false);
  const [selectedSite, setSelectedSite] = useState("");
  const [selectedAdmin, setSelectedAdmin] = useState("");
  const [newThreshold, setNewThreshold] = useState("");

  // Form states for adding new thresholds
  const [adminThresholdForm, setAdminThresholdForm] = useState({ admin_id: '', threshold: '' });
  const [workerThresholdForm, setWorkerThresholdForm] = useState({ site_id: '', worker_id: '', threshold: '' });
  const [showAdminForm, setShowAdminForm] = useState(false);
  const [showWorkerForm, setShowWorkerForm] = useState(false);

  useEffect(() => {
    if (!['company_owner', 'admin'].includes(role)) {
      toast.error("Access denied");
      navigate("/");
      return;
    }
    fetchThresholds();
    // Refresh every 30 seconds to check for low balances
    const interval = setInterval(fetchThresholds, 30000);
    return () => clearInterval(interval);
  }, [role, navigate]);

  const fetchThresholds = async () => {
    try {
      const response = await fetch(`${ApiURL}/getThresholds.php?user_id=${user_id}`);
      const data = await response.json();
      if (data.status === 'success') {
        setThresholds(data);
        checkLowBalances(data);
      }
    } catch (err) {
      console.error("Failed to fetch thresholds:", err);
      toast.error("Failed to fetch thresholds");
    } finally {
      setLoading(false);
    }
  };

  const checkLowBalances = (data) => {
    const alerts = [];
    
    if (data.role === 'company_owner' && data.admins) {
      data.admins.forEach(admin => {
        const balance = admin.net_balance || 0;
        const threshold = Number(admin.threshold_value || 0);
        if (threshold > 0 && balance < threshold) {
          alerts.push({
            type: 'admin',
            user_name: admin.user_name,
            balance: balance,
            threshold: threshold,
            message: `Admin ${admin.user_name}'s balance (Rs. ${balance.toLocaleString()}) is below threshold (Rs. ${threshold.toLocaleString()})`
          });
        }
      });
    } else if (data.role === 'admin' && data.sites) {
      data.sites.forEach(site => {
        if (site.workers) {
          site.workers.forEach(worker => {
            const balance = worker.net_balance || 0;
            const threshold = Number(worker.threshold_value || 0);
            if (threshold > 0 && balance < threshold) {
              alerts.push({
                type: 'worker',
                user_name: worker.user_name,
                site_name: site.site_name,
                balance: balance,
                threshold: threshold,
                message: `Worker ${worker.user_name} at ${site.site_name}'s balance (Rs. ${balance.toLocaleString()}) is below threshold (Rs. ${threshold.toLocaleString()})`
              });
            }
          });
        }
      });
    }
    
    setLowBalanceAlerts(alerts);
  };

  const handleSaveThreshold = async (type, adminId, workerId = null, siteId = null, newValue) => {
    setSaving(true);
    try {
      let payload = {
        company_id: company_id,
        threshold: parseFloat(newValue)
      };

      let url = '';

      if (type === 'admin') {
        url = `${ApiURL}/setAdminThreshold.php`;
        payload.owner_id = user_id;
        payload.admin_id = adminId;
      } else if (type === 'worker') {
        url = `${ApiURL}/setWorkerSiteThreshold.php`;
        payload.admin_id = user_id;
        payload.worker_id = workerId;
        payload.site_id = siteId;
      }

      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const data = await response.json();
      if (data.status === 'success') {
        toast.success(data.message || 'Threshold updated successfully');
        setEditingId(null);
        fetchThresholds();
      } else {
        toast.error(data.message || 'Failed to update threshold');
      }
    } catch (err) {
      console.error("Error saving threshold:", err);
      toast.error("Failed to save threshold");
    } finally {
      setSaving(false);
    }
  };

  const handleAddAdminThreshold = async (e) => {
    e.preventDefault();
    if (!adminThresholdForm.admin_id || !adminThresholdForm.threshold) {
      toast.error("Please fill all fields");
      return;
    }

    await handleSaveThreshold('admin', parseInt(adminThresholdForm.admin_id), null, null, adminThresholdForm.threshold);
    setAdminThresholdForm({ admin_id: '', threshold: '' });
    setShowAdminForm(false);
  };

  const handleAddWorkerThreshold = async (e) => {
    e.preventDefault();
    if (!workerThresholdForm.site_id || !workerThresholdForm.worker_id || !workerThresholdForm.threshold) {
      toast.error("Please fill all fields");
      return;
    }

    await handleSaveThreshold('worker', null, parseInt(workerThresholdForm.worker_id), parseInt(workerThresholdForm.site_id), workerThresholdForm.threshold);
    setWorkerThresholdForm({ site_id: '', worker_id: '', threshold: '' });
    setShowWorkerForm(false);
  };

  const updateThresholdLocal = (id, newValue, type = 'admin') => {
    setThresholds(prev => {
      if (type === 'admin') {
        return {
          ...prev,
          admins: prev.admins.map(a => a.id === id ? { ...a, threshold_value: parseFloat(newValue) } : a)
        };
      }
      // worker-site update logic
      return prev;
    });
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-xl font-semibold">Loading thresholds...</div>
    </div>
  );

  const isOwner = role === 'company_owner';
  const admins = thresholds.admins || [];
  const sites = thresholds.sites || [];

  return (
    <div className="app-shell min-h-screen flex flex-col">
      <Navbar />
      <div className="flex flex-1">
        <Sidebar />
        <main className={`min-w-0 flex-1 transition-all duration-300 mt-16 p-4 sm:p-6 ${isSidebarOpen ? "lg:ml-72" : ""}`}>
          <div className="max-w-7xl mx-auto">
            <section className="page-surface mb-5 p-4 sm:p-5">
              <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-brand-600">Settings</p>
              <h1 className="mt-1 text-2xl font-black text-slate-900">Threshold Management</h1>
              <p className="mt-1 text-sm text-slate-500">Set low-balance alerts for admins and worker-site balances.</p>
            </section>

            {/* Low Balance Alerts Section */}
            {lowBalanceAlerts.length > 0 && (
              <div className="mb-6">
                <h2 className="text-lg font-semibold mb-3 text-red-700">Low Balance Alerts</h2>
                <div className="space-y-3">
                  {lowBalanceAlerts.map((alert, idx) => (
                    <div
                      key={idx}
                      className="bg-red-50 border-2 border-red-400 rounded-lg p-4 shadow-md"
                      style={{ backgroundColor: '#fee2e2', borderColor: '#dc2626' }}
                    >
                      <div className="flex items-center gap-3">
                        <div>
                          <p className="font-semibold text-red-800">{alert.message}</p>
                          <p className="text-sm text-red-700 mt-1">
                            {isOwner ? 'Please allocate funds to this admin.' : 'Please request funds from admin.'}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Company Owner Section - Set Admin Thresholds */}
            {isOwner && (
              <div className="pro-card p-4 sm:p-6 mb-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-semibold text-gray-800">Set Admin Thresholds</h2>
                  <button
                    onClick={() => setShowAdminForm(!showAdminForm)}
                    className="pro-button pro-button-primary px-4 py-2"
                  >
                    + Add Admin Threshold
                  </button>
                </div>

                {/* Add Admin Threshold Form */}
                {showAdminForm && (
                  <form onSubmit={handleAddAdminThreshold} className="bg-brand-50 p-4 rounded-lg mb-6 border border-brand-200">
                    <h3 className="font-semibold mb-4">Add New Admin Threshold</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <select
                        value={adminThresholdForm.admin_id}
                        onChange={(e) => setAdminThresholdForm({...adminThresholdForm, admin_id: e.target.value})}
                        className="border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Select Admin</option>
                        {admins.map(admin => (
                          <option key={admin.id} value={admin.id}>
                            {admin.user_name} (Id: {admin.id})
                          </option>
                        ))}
                      </select>
                      <input
                        type="number"
                        placeholder="Threshold amount"
                        value={adminThresholdForm.threshold}
                        onChange={(e) => setAdminThresholdForm({...adminThresholdForm, threshold: e.target.value})}
                        step="0.01"
                        min="0"
                        className="border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <button
                        type="submit"
                        disabled={saving}
                        className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded transition disabled:bg-gray-400"
                      >
                        {saving ? 'Saving...' : 'Save'}
                      </button>
                    </div>
                  </form>
                )}

                {/* Admin Thresholds Table */}
                {admins.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse">
                      <thead>
                        <tr className="bg-blue-100">
                          <th className="border border-gray-300 px-4 py-2 text-left">Admin Name</th>
                          <th className="border border-gray-300 px-4 py-2 text-left">Current Balance</th>
                          <th className="border border-gray-300 px-4 py-2 text-left">Current Threshold</th>
                          <th className="border border-gray-300 px-4 py-2 text-left">New Threshold</th>
                          <th className="border border-gray-300 px-4 py-2 text-left">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {admins.map(admin => (
                          <tr key={admin.id} className="hover:bg-gray-50 border-b">
                            <td className="border border-gray-300 px-4 py-3">{admin.user_name}</td>
                            <td className="border border-gray-300 px-4 py-3 font-semibold">
                              Rs. {(admin.net_balance || 0).toLocaleString('en-IN', {maximumFractionDigits: 2})}
                              {Number(admin.threshold_value || 0) > 0 && (admin.net_balance || 0) < Number(admin.threshold_value || 0) && (
                                <span className="text-red-600 ml-2 font-bold">LOW</span>
                              )}
                            </td>
                            <td className="border border-gray-300 px-4 py-3">
                              {Number(admin.threshold_value || 0) > 0 ? `Rs. ${Number(admin.threshold_value).toLocaleString('en-IN', {maximumFractionDigits: 2})}` : '-'}
                            </td>
                            <td className="border border-gray-300 px-4 py-3">
                              {editingId === `admin-${admin.id}` ? (
                                <input
                                  type="number"
                                  defaultValue={admin.threshold_value || ''}
                                  onChange={(e) => setEditingValue(e.target.value)}
                                  step="0.01"
                                  min="0"
                                  className="border border-gray-300 rounded px-2 py-1 w-28 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  autoFocus
                                />
                              ) : (
                                <span
                                  onClick={() => {
                                    setEditingId(`admin-${admin.id}`);
                                    setEditingValue(admin.threshold_value || '');
                                  }}
                                  className="cursor-pointer text-blue-600 hover:underline"
                                >
                                  Click to edit
                                </span>
                              )}
                            </td>
                            <td className="border border-gray-300 px-4 py-3">
                              {editingId === `admin-${admin.id}` ? (
                                <div className="flex gap-2">
                                  <button
                                    onClick={() => handleSaveThreshold('admin', admin.id, null, null, editingValue)}
                                    disabled={saving}
                                    className="bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded text-sm transition disabled:bg-gray-400"
                                  >
                                    Save
                                  </button>
                                  <button
                                    onClick={() => setEditingId(null)}
                                    className="bg-gray-400 hover:bg-gray-500 text-white px-3 py-1 rounded text-sm transition"
                                  >
                                    Cancel
                                  </button>
                                </div>
                              ) : (
                                <button
                                  onClick={() => {
                                    setEditingId(`admin-${admin.id}`);
                                    setEditingValue(admin.threshold_value || '');
                                  }}
                                  className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded text-sm transition"
                                >
                                  Edit
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-4">No admins found for your company.</p>
                )}
              </div>
            )}

            {/* Admin Section - Set Worker-Site Thresholds */}
            {!isOwner && (
              <div className="pro-card p-4 sm:p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-semibold text-gray-800">Set Worker-Site Thresholds</h2>
                  <button
                    onClick={() => setShowWorkerForm(!showWorkerForm)}
                    className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition"
                  >
                    + Add Worker Threshold
                  </button>
                </div>

                {/* Add Worker Threshold Form */}
                {showWorkerForm && (
                  <form onSubmit={handleAddWorkerThreshold} className="bg-green-50 p-4 rounded-lg mb-6 border border-green-200">
                    <h3 className="font-semibold mb-4">Add New Worker-Site Threshold</h3>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <select
                        value={workerThresholdForm.site_id}
                        onChange={(e) => {
                          setWorkerThresholdForm({...workerThresholdForm, site_id: e.target.value, worker_id: ''});
                        }}
                        className="border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                      >
                        <option value="">Select Site</option>
                        {sites.map(site => (
                          <option key={site.site_id} value={site.site_id}>
                            {site.site_name}
                          </option>
                        ))}
                      </select>
                      <select
                        value={workerThresholdForm.worker_id}
                        onChange={(e) => setWorkerThresholdForm({...workerThresholdForm, worker_id: e.target.value})}
                        disabled={!workerThresholdForm.site_id}
                        className="border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:bg-gray-100"
                      >
                        <option value="">Select Worker</option>
                        {workerThresholdForm.site_id && sites.find(s => s.site_id === parseInt(workerThresholdForm.site_id))?.workers?.map(worker => (
                          <option key={worker.id} value={worker.id}>
                            {worker.user_name}
                          </option>
                        ))}
                      </select>
                      <input
                        type="number"
                        placeholder="Threshold amount"
                        value={workerThresholdForm.threshold}
                        onChange={(e) => setWorkerThresholdForm({...workerThresholdForm, threshold: e.target.value})}
                        step="0.01"
                        min="0"
                        className="border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                      />
                      <button
                        type="submit"
                        disabled={saving}
                        className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded transition disabled:bg-gray-400"
                      >
                        {saving ? 'Saving...' : 'Save'}
                      </button>
                    </div>
                  </form>
                )}

                {/* Worker-Site Thresholds */}
                {sites.length > 0 ? (
                  <div className="space-y-6">
                    {sites.map(site => (
                      <div key={site.site_id} className="border border-gray-300 rounded-lg p-4">
                        <h3 className="text-lg font-bold mb-4 text-gray-800">{site.site_name}</h3>
                        {site.workers && site.workers.length > 0 ? (
                          <div className="overflow-x-auto">
                            <table className="w-full border-collapse">
                              <thead>
                                <tr className="bg-green-100">
                                  <th className="border border-gray-300 px-4 py-2 text-left">Worker Name</th>
                                  <th className="border border-gray-300 px-4 py-2 text-left">Current Balance</th>
                                  <th className="border border-gray-300 px-4 py-2 text-left">Current Threshold</th>
                                  <th className="border border-gray-300 px-4 py-2 text-left">New Threshold</th>
                                  <th className="border border-gray-300 px-4 py-2 text-left">Action</th>
                                </tr>
                              </thead>
                              <tbody>
                                {site.workers.map(worker => (
                                  <tr key={`${site.site_id}-${worker.id}`} className="hover:bg-gray-50 border-b">
                                    <td className="border border-gray-300 px-4 py-3">{worker.user_name}</td>
                                    <td className="border border-gray-300 px-4 py-3 font-semibold">
                                      Rs. {(worker.net_balance || 0).toLocaleString('en-IN', {maximumFractionDigits: 2})}
                                      {Number(worker.threshold_value || 0) > 0 && (worker.net_balance || 0) < Number(worker.threshold_value || 0) && (
                                        <span className="text-red-600 ml-2 font-bold">LOW</span>
                                      )}
                                    </td>
                                    <td className="border border-gray-300 px-4 py-3">
                                      {Number(worker.threshold_value || 0) > 0 ? `Rs. ${Number(worker.threshold_value).toLocaleString('en-IN', {maximumFractionDigits: 2})}` : '-'}
                                    </td>
                                    <td className="border border-gray-300 px-4 py-3">
                                      {editingId === `worker-${site.site_id}-${worker.id}` ? (
                                        <input
                                          type="number"
                                          defaultValue={worker.threshold_value || ''}
                                          onChange={(e) => setEditingValue(e.target.value)}
                                          step="0.01"
                                          min="0"
                                          className="border border-gray-300 rounded px-2 py-1 w-28 focus:outline-none focus:ring-2 focus:ring-green-500"
                                          autoFocus
                                        />
                                      ) : (
                                        <span
                                          onClick={() => {
                                            setEditingId(`worker-${site.site_id}-${worker.id}`);
                                            setEditingValue(worker.threshold_value || '');
                                          }}
                                          className="cursor-pointer text-green-600 hover:underline"
                                        >
                                          Click to edit
                                        </span>
                                      )}
                                    </td>
                                    <td className="border border-gray-300 px-4 py-3">
                                      {editingId === `worker-${site.site_id}-${worker.id}` ? (
                                        <div className="flex gap-2">
                                          <button
                                            onClick={() => handleSaveThreshold('worker', null, worker.id, site.site_id, editingValue)}
                                            disabled={saving}
                                            className="bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded text-sm transition disabled:bg-gray-400"
                                          >
                                            Save
                                          </button>
                                          <button
                                            onClick={() => setEditingId(null)}
                                            className="bg-gray-400 hover:bg-gray-500 text-white px-3 py-1 rounded text-sm transition"
                                          >
                                            Cancel
                                          </button>
                                        </div>
                                      ) : (
                                        <button
                                          onClick={() => {
                                            setEditingId(`worker-${site.site_id}-${worker.id}`);
                                            setEditingValue(worker.threshold_value || '');
                                          }}
                                          className="bg-green-500 hover:bg-green-600 text-white px-3 py-1 rounded text-sm transition"
                                        >
                                          Edit
                                        </button>
                                      )}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        ) : (
                          <p className="text-gray-500 text-center py-3">No workers assigned to this site.</p>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-4">No sites found for your company.</p>
                )}
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default ThresholdManagement;

